import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { AgentProfileDto } from '@workspace/data-types';

@Component({
  selector: 'etxe-agent-completion-display',
  imports: [MatDividerModule, MatIconModule, MatButtonModule],
  templateUrl: './agent-completion-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentCompletionDisplayComponent {
  readonly profile = input<AgentProfileDto | null | undefined>(undefined);
  readonly showDivider = input<boolean>(true);

  readonly isDismissed = signal<boolean>(false);
  readonly justReached100 = signal<boolean>(false);
  private initialScore: number | null = null;

  constructor() {
    effect(() => {
      const p = this.profile();
      if (!p) return;
      const score = this.profileCompletion();
      if (this.initialScore === null) {
        this.initialScore = score;
      } else if (this.initialScore < 100 && score === 100) {
        this.justReached100.set(true);
      }
    });
  }

  readonly isVisible = computed(() => {
    if (this.isDismissed()) return false;
    const score = this.profileCompletion();
    if (score < 100) return true;
    return this.justReached100();
  });

  onDismiss(): void {
    this.isDismissed.set(true);
  }

  readonly profileCompletion = computed(() => {
    const p = this.profile();
    if (!p) return 0;

    let score = p.cards?.activeCard ? 30 : 0; // Base: Card & Identity validated

    const primaryRegion = p.intervention?.primaryRegion;
    if (primaryRegion) score += 10;

    const zones = p.intervention?.zones;
    const hasDepts = (zones?.[0]?.departments?.length ?? 0) > 0;
    if (hasDepts) score += 20;

    const preferredCities = p.intervention?.preferredCities;
    if (preferredCities && preferredCities.length > 0) score += 15;

    const network = p.activity?.network;
    if (network && network.trim().length > 0) score += 10;

    const specialties = p.activity?.specialties;
    if (specialties && specialties.length > 0) score += 10;

    const bio = p.activity?.bio;
    if (bio && bio.trim().length > 0) score += 5;

    return Math.min(score, 100);
  });

  readonly completionTip = computed(() => {
    const p = this.profile();
    if (!p) return 'Conseil : Complétez vos informations de profil';

    if (!p.cards?.activeCard) {
      return 'Action requise : Votre carte professionnelle T a expiré ou est absente';
    }

    const bio = p.activity?.bio;
    if (!bio || bio.trim().length === 0) {
      return 'Conseil : Rédigez votre biographie pour vous démarquer';
    }

    const network = p.activity?.network;
    if (!network || network.trim().length === 0) {
      return 'Conseil : Renseignez votre réseau ou agence';
    }

    const specialties = p.activity?.specialties;
    if (!specialties || specialties.length === 0) {
      return 'Conseil : Indiquez vos spécialités immobilières';
    }

    const preferredCities = p.intervention?.preferredCities;
    if (!preferredCities || preferredCities.length === 0) {
      return 'Conseil : Ajoutez vos villes et communes cibles';
    }

    const zones = p.intervention?.zones;
    const hasDepts = (zones?.[0]?.departments?.length ?? 0) > 0;
    if (!hasDepts) {
      return "Conseil : Précisez vos départements d'intervention";
    }

    return 'Conseil : Complétez vos informations de profil';
  });
}
